/**
 * BetweenStepsPauseRow — the "Pause between test steps" control shared by
 * all three run-template dialogs (#1642 Slice 2).
 *
 * Pauses before each step (action) inside a test — Playwright's slowMo,
 * named plainly and in parallel with "Pause between tests". Value is in
 * milliseconds (a per-step delay is fine-grained), persisted as the
 * template's `slowMotionMs`. A template setting: 0 means no pause.
 *
 * A plain number field (matching the timeout rows and its "Pause between
 * tests" sibling) rather than a slider — the slider overflowed its
 * half-width grid cell and tripped the dialog's horizontal scrollbar.
 * Shared (rule of three) so label, copy, and clamp can't drift.
 */
import { Info } from 'lucide-react'
import { Label, Input, Tooltip } from '@/components/ui'

export function BetweenStepsPauseRow({ value, onChange, align = 'end' }: {
  value: number
  onChange: (next: number) => void
  /** Which way the info tooltip opens. Defaults to 'end' (opens left)
   * since this control sits in the right column of the pacing pair; pass
   * 'start' if it's ever placed on the left. */
  align?: 'start' | 'end'
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1.5">
        Pause between test steps (ms)
        <Tooltip
          content="Waits this long before each step inside a test. Use it for apps that block or rate-limit rapid automation. 0 = no pause."
          triggerLabel="What is the pause between test steps?"
          align={align}
        >
          <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Tooltip>
      </Label>
      <Input
        type="number"
        min={0}
        step={50}
        placeholder="0"
        value={value}
        onChange={e => {
          // Accept any non-negative integer during typing; clamp on blur.
          const parsed = parseInt(e.target.value, 10)
          onChange(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0)
        }}
        onBlur={() => onChange(Math.max(Math.round(value) || 0, 0))}
        className="w-28"
      />
    </div>
  )
}
